// src/lib/userStorage.js
// User-specific data storage service

/**
 * Get user-specific storage key
 * @param {string} userId - User ID
 * @param {string} key - Storage key
 * @returns {string} - Prefixed storage key
 */
const getUserStorageKey = (userId, key) => {
  return `spcc:user:${userId}:${key}`;
};

/**
 * Save user-specific data
 * @param {string} userId - User ID
 * @param {string} key - Storage key
 * @param {any} data - Data to store
 */
export const saveUserData = (userId, key, data) => {
  try {
    const storageKey = getUserStorageKey(userId, key);
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save user data:', error);
  }
};

/**
 * Load user-specific data
 * @param {string} userId - User ID
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if not found
 * @returns {any} - Stored data or default value
 */
export const loadUserData = (userId, key, defaultValue = null) => {
  try {
    const storageKey = getUserStorageKey(userId, key);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return JSON.parse(stored);
    }
    return defaultValue;
  } catch (error) {
    console.error('Failed to load user data:', error);
    return defaultValue;
  }
};

/**
 * Remove user-specific data
 * @param {string} userId - User ID
 * @param {string} key - Storage key
 */
export const removeUserData = (userId, key) => {
  try {
    const storageKey = getUserStorageKey(userId, key);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('Failed to remove user data:', error);
  }
};

/**
 * Check if user data exists
 * @param {string} userId - User ID
 * @param {string} key - Storage key
 * @returns {boolean} - True if data exists
 */
export const hasUserData = (userId, key) => {
  try {
    const storageKey = getUserStorageKey(userId, key);
    return localStorage.getItem(storageKey) !== null;
  } catch (error) {
    console.error('Failed to check user data:', error);
    return false;
  }
};

/**
 * Clear all data for a specific user
 * @param {string} userId - User ID
 */
export const clearUserData = (userId) => {
  try {
    const prefix = `spcc:user:${userId}:`;
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear user data:', error);
  }
};

/**
 * Migrate legacy data to user-specific storage
 * @param {string} userId - User ID
 */
export const migrateLegacyData = (userId) => {
  try {
    // Migrate issues data
    const legacyIssues = localStorage.getItem('spcc:v1:issues');
    if (legacyIssues) {
      saveUserData(userId, 'issues', JSON.parse(legacyIssues));
      localStorage.removeItem('spcc:v1:issues');
    }

    // Migrate JIRA config
    const legacyJiraConfig = localStorage.getItem('spcc:jira-config');
    if (legacyJiraConfig) {
      saveUserData(userId, 'jira-config', JSON.parse(legacyJiraConfig));
      localStorage.removeItem('spcc:jira-config');
    }

    // Migrate connections
    const legacyConnections = localStorage.getItem('spcc:connections');
    if (legacyConnections) {
      saveUserData(userId, 'connections', JSON.parse(legacyConnections));
      localStorage.removeItem('spcc:connections');
    }

    console.log('Legacy data migrated successfully for user:', userId);
  } catch (error) {
    console.error('Failed to migrate legacy data:', error);
  }
};

/**
 * Export user data for backup
 * @param {string} userId - User ID
 * @returns {object} - All user data
 */
export const exportUserData = (userId) => {
  try {
    const prefix = `spcc:user:${userId}:`;
    const userData = {};
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        const dataKey = key.replace(prefix, '');
        try {
          userData[dataKey] = JSON.parse(localStorage.getItem(key));
        } catch (error) {
          userData[dataKey] = localStorage.getItem(key);
        }
      }
    });

    return userData;
  } catch (error) {
    console.error('Failed to export user data:', error);
    return {};
  }
};

/**
 * Import user data from backup
 * @param {string} userId - User ID
 * @param {object} userData - Data to import
 */
export const importUserData = (userId, userData) => {
  try {
    Object.entries(userData).forEach(([key, value]) => {
      saveUserData(userId, key, value);
    });
    console.log('User data imported successfully for user:', userId);
  } catch (error) {
    console.error('Failed to import user data:', error);
  }
};

// Storage keys constants
export const STORAGE_KEYS = {
  ISSUES: 'issues',
  JIRA_CONFIG: 'jira-config',
  CONNECTIONS: 'connections',
  SETTINGS: 'settings',
  FILTERS: 'filters',
  TIME_REFLECTIONS: 'time-reflections',
  WEEK_DATA: 'week-data',
};